// composables
import { ref, reactive, watch, type Ref } from 'vue'
// utilities
import { alertToast } from '@/utilities/notification'
import Swal from 'sweetalert2'
// stores
import { useAuthStore } from '@/stores/AuthStore'
// composables
import useApiRequest from '@/composables/useApiRequest'
import useVuelidate from '@vuelidate/core'
import { required } from '@vuelidate/validators'
// types
import type { Agreement } from '@/types/domain/agreement'

/**
 * Composable for managing agreement transfer operations
 * Handles fetching dealerships, validation, and transfer submission
 */
export default function useAgreementTransfer(agreementID: Ref<string | number>, agreementData: Ref<Agreement | null>) {
  // stores
  const authStore = useAuthStore()

  // state
  const transferModalIsOpen = ref(false)
  const dealershipsList = ref<Array<{ value: number; label: string; manufacturerLogo: string }> | null>(null)

  const transferAgreementData = reactive({
    from: {
      agreementID: parseInt(String(agreementID.value))
    },
    to: null as number | null,
    note: null as string | null
  })

  // api requests
  const transferAgreement = useApiRequest()
  const dealershipsRequest = useApiRequest()
  const dealershipRequest = useApiRequest()

  // validation
  const transferRules = {
    from: {
      agreementID: { required }
    },
    to: { required },
    note: { required }
  }

  const transferV$ = useVuelidate(transferRules, transferAgreementData)

  // Track if dealerships have been loaded to avoid duplicate requests
  const dealershipsLoaded = ref(false)

  /**
   * Fetches dealerships list for transfer
   */
  function fetchDealerships(): void {
    // Don't fetch if already loaded
    if (dealershipsLoaded.value) {
      return
    }
    dealershipsLoaded.value = true
    const dealershipIds = authStore.accessAllowed.dealerships.map(dealership => dealership.id)

    try {
      dealershipsRequest
        .send({
          method: 'GET',
          endpoint: '/v1/dealerships',
          params: {
            ids: dealershipIds.join(',')
          }
        })
        .then(response => {
          if (response.success) {
            if (!Array.isArray(response.data)) {
              console.error('Response data is not an array', response.data)
              return
            }
            dealershipsList.value = response.data.map(
              (dealership: { id: number; name: string; manufacturerLogo: string }) => {
                return {
                  value: dealership.id,
                  label: dealership.name,
                  manufacturerLogo: dealership.manufacturerLogo
                }
              }
            )
          } else {
            alertToast('Failed to fetch dealerships', response.message, 'error')
          }
        })
    } catch (err) {
      alertToast('Failed to fetch dealerships', (err as Error).message, 'error')
    }
  }

  /**
   * Updates dealership data after transfer
   * @param id - The dealership ID to fetch
   */
  function updateDealershipData(id: number): void {
    try {
      dealershipRequest
        .send({
          method: 'GET',
          endpoint: `/v1/dealerships/${id}`
        })
        .then(response => {
          if (response.success && agreementData.value) {
            agreementData.value.dealership = response.data
          } else {
            alertToast('Failed to fetch dealership data', response.message, 'error')
          }
        })
    } catch (err) {
      alertToast('Failed to fetch dealership data', (err as Error).message, 'error')
    }
  }

  /**
   * Submits transfer agreement request
   */
  async function submitTransferAgreement(): Promise<void> {
    // Manual validation since Vuelidate seems to have conflicts with reactive objects
    if (!transferAgreementData.from?.agreementID) {
      alertToast('Validation failed', 'Source agreement ID is missing', 'error')
      return
    }

    if (!transferAgreementData.to) {
      alertToast('Validation failed', 'Please select a dealership to transfer into', 'error')
      return
    }

    if (!transferAgreementData.note || transferAgreementData.note.trim() === '') {
      alertToast('Validation failed', 'Please add a note explaining the transfer', 'error')
      return
    }

    // Validate with Vuelidate for UI error display, but don't block on it
    await transferV$.value.$validate()

    Swal.fire({
      title: 'Are you sure?',
      text: 'Please triple check before transferring.',
      icon: 'warning',
      reverseButtons: true,
      showCloseButton: true,
      showCancelButton: true,
      confirmButtonText: 'Confirm Transfer',
      confirmButtonColor: 'var(--bg-error-solid)',
      cancelButtonText: 'Cancel',
      cancelButtonColor: 'var(--bg-secondary)'
    }).then(function (response) {
      if (response.isConfirmed && agreementData.value) {
        const dataToPost = {
          to: transferAgreementData.to,
          note: transferAgreementData.note
        }

        try {
          transferAgreement
            .send({
              method: 'POST',
              endpoint: `/v1/agreements/${agreementData.value.id}/transfer`,
              data: JSON.stringify(dataToPost)
            })
            .then(response => {
              if (response.success) {
                alertToast('Customer transferred', null, 'success')
                if (transferAgreementData.to) {
                  updateDealershipData(transferAgreementData.to)
                }
              } else {
                alertToast('Failed to transfer customer', response.message, 'error')
              }
              // Close modal
              transferModalIsOpen.value = false
            })
        } catch (err) {
          alertToast('Failed to transfer customer', (err as Error).message, 'error')
        }
      }
    })
  }

  // Watch for modal opening to fetch dealerships
  watch(
    transferModalIsOpen,
    (isOpen) => {
      if (isOpen && !dealershipsLoaded.value) {
        fetchDealerships()
      }
    }
  )

  return {
    // state
    transferModalIsOpen,
    transferAgreementData,
    dealershipsList,
    transferV$,
    transferAgreement,
    // methods
    fetchDealerships,
    submitTransferAgreement
  }
}
